// ✅ store/reducers/notificationReducer.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ─── Async Thunks ────────────────────────────────────────────

export const fetch_notifications = createAsyncThunk(
    'notification/fetch',
    async ({ page = 1, limit = 20, unreadOnly = false, notifType = '' } = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({ page, limit })
            if (unreadOnly) params.append('unreadOnly', 'true')
            if (notifType)  params.append('notifType', notifType)

            const { data } = await api.get(`/notifications/my?${params}`)
            return { ...data, page }
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta baş verdi')
        }
    }
)

export const fetch_unread_count = createAsyncThunk(
    'notification/unreadCount',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get('/notifications/unread-count')
            return data.unreadCount || 0
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta')
        }
    }
)

export const mark_read = createAsyncThunk(
    'notification/markRead',
    async (id, { rejectWithValue }) => {
        try {
            await api.put(`/notifications/${id}/read`)
            return id
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta')
        }
    }
)

export const mark_all_read = createAsyncThunk(
    'notification/markAllRead',
    async (_, { rejectWithValue }) => {
        try {
            await api.put('/notifications/read-all')
            return true
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta')
        }
    }
)

export const delete_notification = createAsyncThunk(
    'notification/delete',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/notifications/${id}`)
            return id
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta')
        }
    }
)

export const delete_all_notifications = createAsyncThunk(
    'notification/deleteAll',
    async (ids, { rejectWithValue }) => {
        try {
            await Promise.all(ids.map(id => api.delete(`/notifications/${id}`)))
            return ids
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Xəta')
        }
    }
)

// ─── Slice ───────────────────────────────────────────────────

const notificationSlice = createSlice({
    name: 'notification',
    initialState: {
        notifications:   [],   // aktiv siyahı
        unreadCount:     0,
        total:           0,
        page:            1,
        hasMore:         true,
        loading:         false,
        loadingMore:     false,
        countLoading:    false,
        error:           null,
    },
    reducers: {
        // Socket-dən gələn real-time bildiriş
        add_realtime_notification: (state, { payload }) => {
            if (payload?._id && state.notifications.some(n => n._id === payload._id)) return
            state.notifications = [payload, ...state.notifications]
            state.unreadCount   += 1
            state.total         += 1
        },
        // Filter dəyişdikdə siyahını sıfırla
        reset_notifications: (state) => {
            state.notifications = []
            state.page          = 1
            state.hasMore       = true
            state.error         = null
        },
        clear_error: (state) => { state.error = null },
    },
    extraReducers: (builder) => {
        // ── fetch_notifications ───────────────────────────────
        builder
            .addCase(fetch_notifications.pending, (state, action) => {
                if (action.meta.arg?.page > 1) state.loadingMore = true
                else                           state.loading     = true
                state.error = null
            })
            .addCase(fetch_notifications.fulfilled, (state, action) => {
                state.loading     = false
                state.loadingMore = false
                const { notifications, unreadCount, total, page } = action.payload
                const limit = action.meta.arg?.limit || 20
                if (page > 1) {
                    const existingIds = new Set(state.notifications.map(n => n._id))
                    state.notifications = [
                        ...state.notifications,
                        ...notifications.filter(n => !existingIds.has(n._id))
                    ]
                } else {
                    state.notifications = notifications
                }
                state.unreadCount = unreadCount
                state.total       = total
                state.page        = page
                state.hasMore     = notifications.length === limit
            })
            .addCase(fetch_notifications.rejected, (state, { payload }) => {
                state.loading     = false
                state.loadingMore = false
                state.error       = payload
            })

        // ── fetch_unread_count ───────────────────────────────
        builder
            .addCase(fetch_unread_count.pending,   (state)            => { state.countLoading = true  })
            .addCase(fetch_unread_count.fulfilled, (state, { payload }) => {
                state.countLoading = false
                state.unreadCount  = payload
            })
            .addCase(fetch_unread_count.rejected,  (state)            => { state.countLoading = false })

        // ── mark_read ────────────────────────────────────────
        builder.addCase(mark_read.fulfilled, (state, { payload: id }) => {
            const n = state.notifications.find(n => n._id === id)
            if (n && !n.isRead) {
                n.isRead        = true
                state.unreadCount = Math.max(0, state.unreadCount - 1)
            }
        })

        // ── mark_all_read ────────────────────────────────────
        builder.addCase(mark_all_read.fulfilled, (state) => {
            state.notifications = state.notifications.map(n => ({ ...n, isRead: true }))
            state.unreadCount   = 0
        })

        // ── delete_notification ──────────────────────────────
        builder.addCase(delete_notification.fulfilled, (state, { payload: id }) => {
            const n = state.notifications.find(n => n._id === id)
            if (n && !n.isRead) state.unreadCount = Math.max(0, state.unreadCount - 1)
            state.notifications = state.notifications.filter(n => n._id !== id)
            state.total         = Math.max(0, state.total - 1)
        })

        // ── delete_all_notifications ─────────────────────────
        builder.addCase(delete_all_notifications.fulfilled, (state) => {
            state.notifications = []
            state.unreadCount   = 0
            state.total         = 0
            state.hasMore       = false
        })
    }
})

export const {
    add_realtime_notification,
    reset_notifications,
    clear_error
} = notificationSlice.actions

export default notificationSlice.reducer
